import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { Autocomplete } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import { formatPlaceAddress, stripPlusCode } from "../utils/locationMatcher";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";
import { FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaShieldAlt, FaCar, FaCalendarAlt, FaClock, FaArrowRight, FaTag, FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  calculateCabPrice,
  calculateTaxesAndFees,
  calculateTotalAmount,
} from "../utils/priceCalculator";
import { trackAbandonedBooking } from "../utils/abandonedBookingTracker";

const CustomerDetails = () => {
  const { isLoaded } = useGoogleMaps();
  const pickupAutocompleteRef = useRef(null);
  const dropAutocompleteRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [cabDetails, setCabDetails] = useState(null);
  const [distance, setDistance] = useState(null);
  const [rentalPackages, setRentalPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [paymentOption, setPaymentOption] = useState("full");
  const [bookingSettings, setBookingSettings] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [coupons, setCoupons] = useState([]);

  // Form state
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [pickupAddressCity, setPickupAddressCity] = useState("");
  const [dropAddressCity, setDropAddressCity] = useState("");
  const [errors, setErrors] = useState({});
  const [addOnServices, setAddOnServices] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [isFareBreakupOpen, setIsFareBreakupOpen] = useState(true);
  const [isMobileBreakupOpen, setIsMobileBreakupOpen] = useState(false);
  const [isViaStopsOpen, setIsViaStopsOpen] = useState(false);

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const isCorporateBookingFlow = queryParams.get("corporate") === "1" || sessionStorage.getItem("corporateBookingMode") === "1";

  // Force full payment for corporate bookings
  useEffect(() => {
    if (isCorporateBookingFlow) {
      setPaymentOption("full");
    }
  }, [isCorporateBookingFlow]);
  const fromCity = queryParams.get("from");
  const toCity = queryParams.get("to");
  const urlPickup = queryParams.get("pickupAddress") || "";
  const urlDrop = queryParams.get("dropAddress") || "";
  const dbPickup = queryParams.get("dbPickup") || "";
  const dbDrop = queryParams.get("dbDrop") || "";
  const fromParam = queryParams.get("fromParam") || "";
  const toParam = queryParams.get("toParam") || "";
  const pickupAddressCityParam = queryParams.get("pickupAddressCity") || "";
  const dropAddressCityParam = queryParams.get("dropAddressCity") || "";
  const urlAmount = parseFloat(queryParams.get("amount")) || 0;

  // Extract coupon and add-ons from URL parameters for restoration
  const urlCouponCode = queryParams.get("couponCode");
  const urlCouponDiscount = queryParams.get("couponDiscount");
  const urlSelectedAddOns = queryParams.get("selectedAddOns");

  // Set initial pickup and drop addresses
  useEffect(() => {
    if (urlPickup) setPickupAddress(stripPlusCode(urlPickup));
    else if (dbPickup) setPickupAddress(stripPlusCode(dbPickup));
    else if (fromParam) setPickupAddress(stripPlusCode(fromParam));

    if (urlDrop) setDropAddress(stripPlusCode(urlDrop));
    else if (dbDrop) setDropAddress(stripPlusCode(dbDrop));
    else if (toParam) setDropAddress(stripPlusCode(toParam));

    setPickupAddressCity(pickupAddressCityParam || fromCity || "");
    setDropAddressCity(dropAddressCityParam || toCity || "");
  }, [urlPickup, urlDrop, dbPickup, dbDrop, fromParam, toParam, pickupAddressCityParam, dropAddressCityParam]);

  // Restore coupon and add-ons from URL parameters or sessionStorage
  useEffect(() => {
    // Restore applied coupon from URL
    if (urlCouponCode && urlCouponDiscount) {
      const couponData = {
        code: urlCouponCode,
        discount: parseInt(urlCouponDiscount, 10)
      };
      setAppliedCoupon(couponData);
      // Also save to sessionStorage for backup
      sessionStorage.setItem("appliedCoupon", JSON.stringify(couponData));
    } else {
      // If fresh URL without coupon parameter, clear applied coupon
      // Only restore from sessionStorage if explicitly passed in URL
      setAppliedCoupon(null);
      sessionStorage.removeItem("appliedCoupon");
    }

    // Restore selected add-ons from URL
    if (urlSelectedAddOns) {
      try {
        const parsedAddOns = JSON.parse(urlSelectedAddOns);
        if (Array.isArray(parsedAddOns)) {
          setSelectedAddOns(parsedAddOns);
          // Also save to sessionStorage for backup
          sessionStorage.setItem("selectedAddOns", urlSelectedAddOns);
        }
      } catch (error) {
        console.error("Error parsing selected add-ons from URL:", error);
      }
    } else {
      // If fresh URL without selectedAddOns parameter, clear add-ons
      // Only restore from sessionStorage if explicitly passed in URL
      setSelectedAddOns([]);
      sessionStorage.removeItem("selectedAddOns");
    }
  }, [urlCouponCode, urlCouponDiscount, urlSelectedAddOns]);

  let distanceParam = 0;
  const fromLower = fromCity?.toLowerCase();
  const toLower = toCity?.toLowerCase();

  if ((fromLower === "rajkot" && toLower === "delhi") || (fromLower === "delhi" && toLower === "rajkot")) {
    distanceParam = 1140;
  } else {
    const distanceStr = queryParams.get("distance");
    distanceParam = distanceStr ? parseFloat(distanceStr) : 0;
  }

  const cabId = queryParams.get("cabId");
  const bookingType = queryParams.get("tripType") || queryParams.get("type");
  const travelDate = queryParams.get("date");
  const travelTime = queryParams.get("time");
  const serviceType = queryParams.get("service");
  const packageName = decodeURIComponent(queryParams.get("package") || "");
  const cityName = decodeURIComponent(queryParams.get("city") || "");
  const airportDirection = queryParams.get("direction");
  const cabNameFromUrl = decodeURIComponent(queryParams.get("cabName") || "");
  const imageUrlFromUrl = decodeURIComponent(queryParams.get("imageUrl") || "");
  const viaCitiesParam = queryParams.get("via");
  const viaCities = viaCitiesParam
    ? (viaCitiesParam.includes("|") ? viaCitiesParam.split("|") : [viaCitiesParam])
        .map((city) => city.trim())
        .filter(Boolean)
    : [];

  const { API_URL } = require("../config/apiConfig");
  const authState = useSelector((state) => state.auth);
  // Normalize user object - could be in .data, .user, or the object itself
  const user = authState.user?.data || authState.user?.user || authState.user;

  // Auto-fill user details
  useEffect(() => {
    const isCorporateBookingFlow = queryParams.get("corporate") === "1" || sessionStorage.getItem("corporateBookingMode") === "1";
    const urlName = queryParams.get("name") || localStorage.getItem("prefill_booking_name") || "";
    const urlMobile = queryParams.get("mobile") || queryParams.get("phone") || localStorage.getItem("prefill_booking_phone") || "";
    const urlEmail = queryParams.get("email") || localStorage.getItem("prefill_booking_email") || "";
    if (localStorage.getItem("prefill_booking_phone")) localStorage.removeItem("prefill_booking_phone");
    if (localStorage.getItem("prefill_booking_name")) localStorage.removeItem("prefill_booking_name");
    if (localStorage.getItem("prefill_booking_email")) localStorage.removeItem("prefill_booking_email");
    const urlGender = queryParams.get("gender");

    console.log("Auto-filling user details. Corporate booking:", isCorporateBookingFlow);

    // Prioritize URL parameters if they exist (usually passed from CarSelection)
    if (urlName) setFullName(urlName);
    if (urlMobile) setMobile(urlMobile);
    if (urlEmail) setEmail(urlEmail);
    if (urlGender) setGender(urlGender.toLowerCase());

    if (urlName || urlMobile) return;

    if (isCorporateBookingFlow) {
      try {
        const corporateBookingFor = sessionStorage.getItem("corporateBookingFor");
        if (corporateBookingFor === 'staff') {
          const staffName = sessionStorage.getItem("selectedStaffName");
          const staffMobile = sessionStorage.getItem("selectedStaffMobile");
          const staffEmail = sessionStorage.getItem("selectedStaffEmail");
          console.log("[CustomerDetails] Prefilling selected staff details:", { staffName, staffMobile, staffEmail });
          if (staffName) setFullName(staffName);
          if (staffMobile) setMobile(String(staffMobile));
          if (staffEmail) setEmail(staffEmail);
          return;
        } else if (corporateBookingFor === 'guest') {
          console.log("[CustomerDetails] Booking for Guest. Clearing inputs.");
          setFullName("");
          setMobile("");
          setEmail("");
          return;
        }

        const corporateMemberRaw = localStorage.getItem("corporateMember");
        const corporateMember = corporateMemberRaw ? JSON.parse(corporateMemberRaw) : null;
        console.log("Corporate member from localStorage:", corporateMember);

        if (corporateMember) {
          // Set fullName from corporate member or fallback to user data
          const corpName = corporateMember.fullName || corporateMember.name;
          if (corpName) {
            console.log("Setting fullName from corporate:", corpName);
            setFullName(corpName);
          }

          // Set email
          if (corporateMember.email) {
            console.log("Setting email from corporate:", corporateMember.email);
            setEmail(corporateMember.email);
          }

          // Set mobile from corporate member
          const corpMobile = corporateMember.mobileNumber || corporateMember.phone || corporateMember.mobile;
          if (corpMobile) {
            console.log("Setting mobile from corporate:", corpMobile);
            setMobile(String(corpMobile));
            localStorage.setItem("userMobile", String(corpMobile));
          }

          // Set gender if available
          if (corporateMember.gender) {
            console.log("Setting gender from corporate:", corporateMember.gender);
            setGender(corporateMember.gender);
          }

          // If we got at least name or mobile from corporate, we're good
          // But we still need to check for gender from other sources
          if ((corpName || corpMobile) && !corporateMember.gender) {
            // Try to get gender from regular user data
            if (user?.gender) {
              console.log("Setting gender from user data:", user.gender);
              setGender(user.gender);
            } else {
              // Try from localStorage
              const savedGender = localStorage.getItem("userGender");
              if (savedGender) {
                console.log("Setting gender from localStorage:", savedGender);
                setGender(savedGender);
              }
            }
          }

          // If we got at least name or mobile from corporate, return
          if (corpName || corpMobile) {
            return;
          }
        }
      } catch (e) {
        console.error("Error parsing corporateMember:", e);
      }
    }

    // Fallback to regular user data
    if (user && (user.phone || user.mobile)) {
      const userPhone = user.phone || user.mobile;
      console.log("Setting mobile from user:", userPhone);
      setMobile(userPhone);
      localStorage.setItem("userMobile", userPhone);
      if (user.name || user.fullName) {
        console.log("Setting fullName from user:", user.name || user.fullName);
        setFullName(user.name || user.fullName);
      }
      if (user.email) {
        console.log("Setting email from user:", user.email);
        setEmail(user.email);
      }
      if (user.gender) {
        console.log("Setting gender from user:", user.gender);
        setGender(user.gender);
      }
      return;
    }

    const savedMobile = localStorage.getItem("userMobile");
    if (savedMobile) {
      console.log("Setting mobile from localStorage:", savedMobile);
      setMobile(savedMobile);
      const savedName = localStorage.getItem("userName");
      const savedEmail = localStorage.getItem("userEmail");
      const savedGender = localStorage.getItem("userGender");
      if (savedName) setFullName(savedName);
      if (savedEmail) setEmail(savedEmail);
      if (savedGender) setGender(savedGender);
      return;
    }

    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log("Parsed user from localStorage:", parsedUser);
        if (parsedUser.phone || parsedUser.mobile) {
          const phone = parsedUser.phone || parsedUser.mobile;
          setMobile(phone);
          localStorage.setItem("userMobile", phone);
        }
        if (parsedUser.name || parsedUser.fullName) {
          setFullName(parsedUser.name || parsedUser.fullName);
        }
        if (parsedUser.email) {
          setEmail(parsedUser.email);
        }
        if (parsedUser.gender) {
          setGender(parsedUser.gender);
        }
      } catch (e) { }
    }
  }, [authState, user]);

  // Fetch corporate profile if needed for corporate bookings
  useEffect(() => {
    const isCorporateBookingFlow = queryParams.get("corporate") === "1" || sessionStorage.getItem("corporateBookingMode") === "1";
    if (!isCorporateBookingFlow) return;

    // If we already have all details including gender, no need to fetch
    if (fullName && mobile && gender) return;

    const fetchCorporateProfile = async () => {
      try {
        const corporateToken = localStorage.getItem("corporateToken");
        if (!corporateToken) return;

        console.log("Fetching corporate profile for missing details...");
        const response = await axios.get(`${API_URL}/corporate/me`, {
          headers: { Authorization: `Bearer ${corporateToken}` }
        });

        if (response.data?.success && response.data?.data) {
          const profile = response.data.data;
          console.log("Corporate profile fetched:", profile);

          if (!fullName && (profile.fullName || profile.name)) {
            console.log("Setting fullName from API:", profile.fullName || profile.name);
            setFullName(profile.fullName || profile.name);
          }
          if (!mobile && (profile.mobileNumber || profile.phone || profile.mobile)) {
            const phone = String(profile.mobileNumber || profile.phone || profile.mobile);
            console.log("Setting mobile from API:", phone);
            setMobile(phone);
            localStorage.setItem("userMobile", phone);
          }
          if (!email && profile.email) {
            console.log("Setting email from API:", profile.email);
            setEmail(profile.email);
          }
          if (!gender && profile.gender) {
            console.log("Setting gender from API:", profile.gender);
            setGender(profile.gender.toLowerCase());
            localStorage.setItem("userGender", profile.gender.toLowerCase());
          }
        }
      } catch (error) {
        console.error("Error fetching corporate profile:", error);
      }
    };

    fetchCorporateProfile();
  }, [API_URL, fullName, mobile, email, gender]);

  // Debounced lead tracking when customer details are entered
  useEffect(() => {
    const cleanPhone = String(mobile || "").replace(/\D/g, "");
    if (cleanPhone.length >= 10) {
      const timer = setTimeout(() => {
        try {
          trackAbandonedBooking({
            name: fullName,
            phone: cleanPhone,
            email: email,
            pickupLocation: { address: pickupAddress || fromCity || "" },
            dropLocation: { address: dropAddress || toCity || "" },
            tripType: bookingType || "one-way",
            pickupDate: travelDate || "",
            pickupTime: travelTime || "",
            selectedCab: cabDetails ? {
              cabId: cabDetails._id,
              cabName: cabDetails.name,
              price: urlAmount || 0,
            } : undefined,
            estimatedPrice: urlAmount || 0,
            exitPage: "/booking/customer-details",
          });
        } catch (err) {
          console.warn("Lead tracking silent error:", err);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [mobile, fullName, email, pickupAddress, dropAddress, cabDetails, urlAmount, bookingType, travelDate, travelTime]);

  // Fetch cab details
  useEffect(() => {
    const fetchCabDetails = async () => {
      if (!cabId) {
        if (cabNameFromUrl) {
          setCabDetails({
            name: cabNameFromUrl,
            description: "Comfortable ride for your journey",
            seatingCapacity: 4,
            basePrice: urlAmount,
            imageUrl: imageUrlFromUrl,
          });
        }
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/cab/${cabId}`);
        const cabData = response.data?.data;
        if (cabData) {
          // Ensure basePrice is set from URL as fallback
          setCabDetails({
            ...cabData,
            basePrice: cabData.basePrice || urlAmount
          });
        }
      } catch (error) {
        // Fallback to URL params if API fails
        if (cabNameFromUrl) {
          setCabDetails({
            name: cabNameFromUrl,
            description: "Comfortable ride for your journey",
            seatingCapacity: 4,
            basePrice: urlAmount,
            imageUrl: imageUrlFromUrl,
          });
        }
      }
    };
    fetchCabDetails();
  }, [cabId, API_URL, cabNameFromUrl, urlAmount]);

  // Fetch rental packages
  useEffect(() => {
    if (serviceType === "rental") {
      const fetchRentalPackages = async () => {
        try {
          const response = await axios.get(`${API_URL}/rental-packages`);
          if (response.data && Array.isArray(response.data.data)) {
            setRentalPackages(response.data.data);
            const matchedPackage = response.data.data.find((pkg) => pkg.name === packageName);
            if (matchedPackage) setSelectedPackage(matchedPackage);
          }
        } catch (error) {
          console.error("Error fetching rental packages:", error);
        }
      };
      fetchRentalPackages();
    }
  }, [serviceType, packageName, API_URL]);

  // Fetch booking settings
  useEffect(() => {
    const fetchBookingSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/booking/settings/public`);
        const data = response.data?.data || response.data?.success ? response.data.data : response.data;
        if (data) {
          setBookingSettings(data);
        }
      } catch (error) {
        // Silently fail - use defaults
      }
    };
    fetchBookingSettings();
  }, [API_URL]);
  const calculatePriceBreakdown = () => {
    // Use the urlAmount extracted at component level
    let basePrice = 0;

    if (serviceType === "rental" && selectedPackage) {
      basePrice = cabDetails?.basePrice || urlAmount || 0;
      if (selectedPackage.serviceCities) {
        for (const serviceCity of selectedPackage.serviceCities) {
          if (serviceCity.cabPrices && cabDetails?.name) {
            const cabPrices = serviceCity.cabPrices;
            const price = cabPrices[cabDetails.name] || cabPrices[cabDetails.name.toUpperCase()] || cabPrices[cabDetails.name.replace(/\s*\(AC\)/i, "")];
            if (price && price > 0) {
              basePrice = price;
              break;
            }
          }
        }
      }
      // Ensure we have a valid basePrice
      if (!basePrice && urlAmount > 0) {
        basePrice = urlAmount;
      }
    } else if (serviceType === "airport" && urlAmount > 0) {
      basePrice = urlAmount;
    } else if (urlAmount > 0) {
      basePrice = urlAmount;
    }

    // Step 1: Base Price
    // Step 2: Apply coupon discount on base fare
    const couponDiscount = appliedCoupon ? appliedCoupon.discount : 0;
    const discountedFare = Math.max(0, basePrice - couponDiscount);

    // Step 3 & 4: Calculate GST on the discounted fare
    const taxRate = 0.18;
    const gst = Math.round(discountedFare * taxRate);
    const fareAfterTax = discountedFare + gst;

    // Step 5: Add add-ons
    const addOnsCost = getAddOnsTotalCost();

    // Step 6: Final total
    const finalTotal = fareAfterTax + addOnsCost;

    return {
      basePrice,
      couponDiscount,
      discountedFare,
      gst,
      fareAfterTax,
      addOnsCost,
      finalTotal
    };
  };

  // Calculate total add-on cost
  const getAddOnsTotalCost = () => {
    return selectedAddOns.reduce((total, service) => total + (service.price || 0), 0);
  };

  const priceBreakdown = calculatePriceBreakdown();
  const { basePrice, couponDiscount, discountedFare, gst, fareAfterTax, addOnsCost, finalTotal } = priceBreakdown;

  const getPartialPayAmount = () => {
    const percentage = bookingSettings && typeof bookingSettings.advancePercentage === 'number'
      ? bookingSettings.advancePercentage
      : 20; // fallback to 20% default if not loaded
    return Math.round(finalTotal * (percentage / 100));
  };

  const partialAmount = getPartialPayAmount();
  const amountToPay = paymentOption === "partial" ? partialAmount : finalTotal;

  const freeCancellationTimeStr = bookingSettings && typeof bookingSettings.freeCancellationHoursBeforeDeparture === 'number'
    ? `${bookingSettings.freeCancellationHoursBeforeDeparture} hr${bookingSettings.freeCancellationHoursBeforeDeparture > 1 ? 's' : ''}`
    : "1 hr";

  const driverDetailsShareTimeStr = bookingSettings && typeof bookingSettings.shareCabDriverDetailsMinutesBefore === 'number'
    ? `${bookingSettings.shareCabDriverDetailsMinutesBefore} mins`
    : "30 mins";

  // Fetch add-on services for the selected cab only
  useEffect(() => {
    const fetchAddOnServices = async () => {
      if (!cabId) {
        setAddOnServices([]);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/cab/${cabId}`);
        console.log("Cab details response:", response.data);

        // Handle different response structures
        let cabData = response.data?.data || response.data;

        if (cabData && typeof cabData === 'object') {
          // Get add-on services from the selected cab
          const addOns = cabData.addOnServices || [];
          console.log("Add-on services found:", addOns);

          if (Array.isArray(addOns) && addOns.length > 0) {
            setAddOnServices(addOns);
          } else {
            console.log("No add-on services available for this cab");
            setAddOnServices([]);
          }
        } else {
          setAddOnServices([]);
        }
      } catch (error) {
        console.error("Error fetching add-on services:", error);
        setAddOnServices([]);
      }
    };
    fetchAddOnServices();
  }, [API_URL, cabId]);

  // Fetch coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("corporateToken");
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const response = await axios.get(`${API_URL}/coupons/available`, config);

        // Handle different response structures
        let couponData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            couponData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            couponData = response.data.data;
          } else if (response.data.coupons && Array.isArray(response.data.coupons)) {
            couponData = response.data.coupons;
          } else if (response.data.success && response.data.data) {
            couponData = response.data.data;
          }
        }

        setCoupons(couponData);
      } catch (error) {
        // Silently fail - fallback coupons will be shown
      }
    };
    fetchCoupons();
  }, [API_URL]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }
    const code = couponCode.trim().toUpperCase();

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("corporateToken");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.post(
        `${API_URL}/coupons/validate`,
        {
          code,
          bookingAmount: basePrice,
          cabType: cabDetails?.name || cabDetails?.type || "all"
        },
        config
      );

      if (response.data.success || response.data.coupon) {
        const validatedCoupon = response.data.coupon || response.data.data;
        const couponData = {
          code: validatedCoupon.code,
          discount: validatedCoupon.discountAmount,
          description: validatedCoupon.description
        };
        setAppliedCoupon(couponData);
        setCouponCode("");
        toast.success(`Coupon ${validatedCoupon.code} applied successfully!`);
        sessionStorage.setItem("appliedCoupon", JSON.stringify(couponData));
        return;
      }
    } catch (error) {
      console.log("Coupon validation API error:", error);
      if (error.response && (error.response.status === 400 || error.response.status === 404)) {
        const message = error.response.data?.message || "Invalid coupon code";
        toast.error(`For this ride, this coupon is not valid: ${message}`);
        return;
      }
    }

    // Fallbacks
    let couponData = null;
    if (code === "MMTDEAL") {
      couponData = { code: "MMTDEAL", discount: 161 };
      setAppliedCoupon(couponData);
      setCouponCode("");
      toast.success("Coupon applied successfully!");
    } else if (code === "CAB10") {
      const discount = Math.round(basePrice * 0.1);
      couponData = { code: "CAB10", discount };
      setAppliedCoupon(couponData);
      setCouponCode("");
      toast.success("10% discount applied!");
    } else {
      toast.error("For this ride, this coupon is not valid: Invalid coupon code");
      return;
    }

    if (couponData) {
      sessionStorage.setItem("appliedCoupon", JSON.stringify(couponData));
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    sessionStorage.removeItem("appliedCoupon");
    toast.success("Coupon removed");
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = "Full name is required";
    if (!mobile.trim() || !/^[6-9]\d{9}$/.test(mobile)) newErrors.mobile = "Valid 10-digit mobile number is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Valid email is required";
    if (!gender) newErrors.gender = "Gender is required";
    if (!pickupAddress.trim()) newErrors.pickupAddress = "Pickup address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle add-on selection toggle
  const toggleAddOn = (service) => {
    setSelectedAddOns(prev => {
      const isSelected = prev.some(s => s.name === service.name);
      let updated;
      if (isSelected) {
        updated = prev.filter(s => s.name !== service.name);
      } else {
        updated = [...prev, service];
      }
      // Update sessionStorage with new add-ons
      if (updated.length > 0) {
        sessionStorage.setItem("selectedAddOns", JSON.stringify(updated));
      } else {
        sessionStorage.removeItem("selectedAddOns");
      }
      return updated;
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      if (!toast.isActive("customer-form-error")) {
        toast.error("Please fill in all required fields", { toastId: "customer-form-error" });
      }
      return;
    }
    setIsLoading(true);
    try {
      localStorage.setItem("userName", fullName);
      localStorage.setItem("userMobile", mobile);
      if (email) localStorage.setItem("userEmail", email);
      if (gender) localStorage.setItem("userGender", gender);

      const params = new URLSearchParams(location.search);
      params.set("name", fullName);
      params.set("mobile", mobile);
      params.set("email", email || "");
      params.set("gender", gender);
      params.set("pickupAddress", pickupAddress);
      params.set("dropAddress", dropAddress || "");
      params.set("paymentOption", paymentOption);

      // Check if this is a corporate booking flow
      const isCorporateBookingFlow = queryParams.get("corporate") === "1" || sessionStorage.getItem("corporateBookingMode") === "1";
      const corporateBookingFor = sessionStorage.getItem("corporateBookingFor"); // 'myself', 'staff', or 'guest'
      const selectedStaffId = sessionStorage.getItem("selectedStaffId");

      if (isCorporateBookingFlow && corporateBookingFor) {
        params.set("bookingFor", corporateBookingFor);
        console.log("[CustomerDetails] Setting bookingFor:", corporateBookingFor);

        // If booking for staff, add the staff member ID
        if (corporateBookingFor === 'staff' && selectedStaffId) {
          params.set("passengerId", selectedStaffId);
          console.log("[CustomerDetails] Setting passengerId (staffId):", selectedStaffId);
        }
      }

      if (appliedCoupon) {
        params.set("couponCode", appliedCoupon.code);
        params.set("couponDiscount", appliedCoupon.discount.toString());
      }
      if (selectedAddOns.length > 0) {
        params.set("selectedAddOns", JSON.stringify(selectedAddOns));
        params.set("addOnsCost", getAddOnsTotalCost().toString());
      }

      // Track lead moving to payment page
      try {
        trackAbandonedBooking({
          name: fullName,
          phone: mobile,
          email: email,
          pickupLocation: { address: pickupAddress || fromCity || "" },
          dropLocation: { address: dropAddress || toCity || "" },
          tripType: bookingType || "one-way",
          pickupDate: travelDate || "",
          pickupTime: travelTime || "",
          selectedCab: cabDetails ? {
            cabId: cabDetails._id,
            cabName: cabDetails.name,
            price: urlAmount || 0,
          } : undefined,
          estimatedPrice: urlAmount || 0,
          exitPage: "/booking/payment",
        });
      } catch (trackErr) {
        console.warn("Lead tracking silent error:", trackErr);
      }

      navigate(`/booking/payment?${params.toString()}`);
    } catch (error) {
      console.error("Error processing booking:", error);
      toast.error("Failed to process booking. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onPlaceChangedPickup = () => {
    if (pickupAutocompleteRef.current !== null) {
      const place = pickupAutocompleteRef.current.getPlace();
      if (place) {
        const fullAddr = formatPlaceAddress(place);
        if (fullAddr) setPickupAddress(fullAddr);
      }
    }
  };

  const onPlaceChangedDrop = () => {
    if (dropAutocompleteRef.current !== null) {
      const place = dropAutocompleteRef.current.getPlace();
      if (place) {
        const fullAddr = formatPlaceAddress(place);
        if (fullAddr) setDropAddress(fullAddr);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const getTripTypeDisplay = () => {
    if (serviceType === "rental") return "Local Rental";
    if (serviceType === "airport") return "Airport Transfer";
    return bookingType === "roundTrip" ? "Round Trip" : "One Way";
  };

  return (
    <section className="bg-white min-h-screen relative">
      {/* MOBILE HEADER (MakeMyTrip Style) - FIXED to viewport top */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-md">
        <div className="flex items-center px-4 py-1.5 gap-2">
          <button 
            type="button"
            onClick={() => navigate(-1)} 
            className="text-white hover:text-gray-200 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-bold !text-sm leading-tight truncate">
              Review your Booking
            </p>
            <p className="text-[11px] text-blue-200 font-semibold mt-0.5 truncate">
              {serviceType === "rental" ? (
                `Local Rental in ${fromCity?.split(",")[0] || fromCity}`
              ) : (
                `${fromCity?.split(",")[0] || fromCity} to ${toCity?.split(",")[0] || toCity}`
              )}
              {travelDate ? ` • ${formatDateDDMMYYYY(travelDate)}` : ""}
              {travelTime ? ` • ${convertTo12HourFormat(decodeURIComponent(travelTime))}` : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1190px] mx-auto pt-[48px] md:pt-4 pb-16 md:pb-8 px-4 lg:px-8">
        <div className="hidden md:flex items-center gap-3 mb-6 bg-blue-600 rounded-md py-3 px-6 text-white">
          <button 
            type="button"
            onClick={() => navigate(-1)} 
            className="hover:bg-blue-700/50 p-1.5 rounded-full transition-colors flex items-center justify-center flex-shrink-0"
            aria-label="Go Back"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold">Review Booking</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT SIDE */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Summary Card */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              {/* Top Light Grey Section */}
              <div className="bg-gray-100 px-6 py-4">
                <p className="text-sm text-gray-600 mb-2">
                  {serviceType === "rental" ? "Local Rental" : serviceType === "airport" ? "Airport Transfer" : bookingType === "roundTrip" ? "Round Trip" : "Outstation One Way Trip"}
                </p>

                {/* Route */}
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  {/* From Address */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start md:items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 md:mt-0 flex-shrink-0"></span>
                      <p className="text-[14px] md:text-[16px] font-semibold text-gray-900 leading-tight break-words">
                        {fromCity || pickupAddressCity || "From"}
                      </p>
                    </div>
                  </div>

                  {/* Arrow - Centered */}
                  <div className="hidden md:flex items-center gap-2 flex-shrink-0 mx-1">
                    <span className="w-10 h-[2px] bg-gray-400"></span>
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                  </div>

                  {/* To Address */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start md:items-center gap-2 md:justify-end">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 md:mt-0 flex-shrink-0 md:hidden"></span>
                      <p className="text-[14px] md:text-[16px] font-semibold text-gray-900 leading-tight break-words md:text-right">
                        {toCity || dropAddressCity || (serviceType === "rental" ? "Local" : "To")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Via Cities Dropdown */}
                {viaCities.length > 0 && (
                  <div className="mt-3 pt-2.5 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setIsViaStopsOpen((prev) => !prev)}
                      className="inline-flex items-center gap-2.5 py-1 px-2.5 -ml-2 rounded-lg hover:bg-orange-50/70 transition-colors text-left group cursor-pointer"
                    >
                      <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider">
                        <FaMapMarkerAlt className="text-[#f26a21] text-xs" />
                        <span>Via Stops ({viaCities.length})</span>
                      </div>
                      <div className="flex items-center gap-1 text-[11px] text-gray-500 group-hover:text-[#f26a21] font-semibold transition-colors">
                        <span>{isViaStopsOpen ? "Hide" : "View"}</span>
                        {isViaStopsOpen ? (
                          <FaChevronUp className="text-[10px]" />
                        ) : (
                          <FaChevronDown className="text-[10px]" />
                        )}
                      </div>
                    </button>

                    {isViaStopsOpen && (
                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        {viaCities.map((city, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-gray-800 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-xs">
                              <span className="w-4 h-4 rounded-full bg-[#f26a21] text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                                {idx + 1}
                              </span>
                              <span className="break-words">{city}</span>
                            </span>
                            {idx < viaCities.length - 1 && (
                              <span className="text-gray-400 font-bold text-sm select-none">→</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Date & Time */}
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-700">
                  <div className="flex items-center gap-1">
                    <FaClock className="text-gray-500" />
                    <span>{travelTime ? convertTo12HourFormat(decodeURIComponent(travelTime)) : "Time not set"}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <FaCalendarAlt className="text-gray-500" />
                    <span>{travelDate ? formatDateDDMMYYYY(travelDate) : "Date not set"}</span>
                  </div>
                </div>
              </div>

              {/* Bottom White Section */}
              <div className="flex justify-between items-center px-6 py-1 gap-3">

                {/* Car Info */}
                <div className="leading-none">
                  <h3 className="text-lg font-bold text-gray-900">
                    {cabDetails?.name || cabNameFromUrl || "Selected Cab"}
                  </h3>
                  <p className="text-sm text-gray-500 leading-snug">{cabDetails?.model || "Or equivalent"}</p>
                  <p className="text-sm text-gray-700 leading-snug">
                    {cabDetails?.seatingCapacity || 4} Seats | {cabDetails?.acType === 'AC' || cabDetails?.ac === true || cabDetails?.airConditioned === true || cabNameFromUrl?.includes('(AC)') || cabDetails?.name?.includes('(AC)') ? 'AC' : 'Non-AC'}
                  </p>
                </div>

                {/* Car Image + Fuel Badge */}
                {(cabDetails || imageUrlFromUrl) && (
                  <div className="flex flex-col items-center bg-white rounded-lg shadow-md border border-gray-200 p-1 min-w-[100px]">
                    <img
                      src={cabDetails?.imageUrl || cabDetails?.image || imageUrlFromUrl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='60'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ECab%3C/text%3E%3C/svg%3E"}
                      alt={cabDetails?.name || cabNameFromUrl}
                      className="w-28 h-20 object-contain"
                      onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='60'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ECab%3C/text%3E%3C/svg%3E"; }}
                    />
                    <span className="bg-orange-100 text-orange-600 text-sm font-semibold px-3 py-1 rounded-full mt-1 leading-tight">
                      {cabDetails?.fuelType || "Petrol"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Inclusions */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">

              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-600 tracking-wide">
                  INCLUSIONS
                </h3>
              </div>

              {/* List Items */}
              <div className="divide-y divide-gray-200">

                {/* 1 - Distance/KM */}
                <div className="flex items-start gap-4 px-6 py-4">
                  <img src="/icons/km.png" alt="km" className="w-8 h-8 object-contain" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {serviceType === "rental" && selectedPackage
                        ? `${selectedPackage.durationHours || 4} Hrs / ${selectedPackage.kilometers || 20} Km included`
                        : serviceType === "airport"
                          ? "Airport transfer included"
                          : `${Math.max(1, Math.round(distanceParam || 0))} Km included`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {serviceType === "rental" && selectedPackage
                        ? `Extra km will be charged at ₹${selectedPackage.extraKmCharge || 10}/km`
                        : serviceType === "airport"
                          ? "Includes pickup/drop from airport"
                          : `₹${cabDetails?.pricePerKm || 10}/km will apply beyond the included kms`}
                    </p>
                  </div>
                </div>

                {/* 2 - Toll/Tax */}
                <div className="flex items-start gap-4 px-6 py-4">
                  <img src="/icons/toll.png" alt="toll" className="w-8 h-8 object-contain" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Toll, tax and other charges
                    </p>
                    <p className="text-sm text-gray-500">
                      {serviceType === "rental"
                        ? "Parking charges are not included"
                        : "Toll, State Tax, Parking charges are included"}
                    </p>
                  </div>
                </div>

                {/* 3 - Driver Allowance */}
                <div className="flex items-start gap-4 px-6 py-4">
                  <img src="/icons/driver.png" alt="driver" className="w-8 h-8 object-contain" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {serviceType === "rental" ? "Driver charges included" : "Driver allowance"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {serviceType === "rental"
                        ? `Included for ${selectedPackage?.durationHours || 4} hours`
                        : "Driver food and accommodation charges are included"}
                    </p>
                  </div>
                </div>

                {/* 4 - Waiting Time */}
                <div className="flex items-start gap-4 px-6 py-4">
                  <img src="/icons/wait.png" alt="waiting" className="w-8 h-8 object-contain" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Waiting time upto {serviceType === "airport" ? "60" : "45"} min for pick up
                    </p>
                    <p className="text-sm text-gray-500">
                      ₹100/30 mins post {serviceType === "airport" ? "60" : "45"} mins
                    </p>
                  </div>
                </div>

                {/* 5 - Fuel */}
                <div className="flex items-start gap-4 px-6 py-4">
                  <img src="/icons/fuel.png" alt="fuel" className="w-8 h-8 object-contain" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Fuel charges included
                    </p>
                    <p className="text-sm text-gray-500">
                      {serviceType === "rental"
                        ? `Fuel cost for ${selectedPackage?.kilometers || 20} km is included`
                        : "Fuel cost for your trip is included in the fare"}
                    </p>
                  </div>
                </div>

              </div>

              {/* Policies Footer */}
              <div className="px-6 py-4 border-t border-gray-200">
                <Link to="/privacy" className="flex items-center justify-between text-sm text-blue-600 font-bold hover:underline cursor-pointer group">
                  <span>Privacy Policy</span>
                  <svg className="w-4 h-4 text-blue-600 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

            </div>

            {/* Cancellation Policy */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
              {/* Top Row */}
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cancellation Policy
                </h3>
                <Link to="/cancellation-policy" className="text-blue-600 text-sm font-medium hover:underline">
                  View Cancellation Policy
                </Link>
              </div>

              {/* Cancellation Info */}
              <p className="text-sm">
                <span className="text-green-600 font-semibold">
                  Free cancellation until
                </span>{" "}
                <span className="text-gray-900 font-medium">
                  {freeCancellationTimeStr} before pickup
                </span>{" "}
                <span className="text-gray-500">
                  | {serviceType === "rental" ? "Full refund on cancellation" : serviceType === "airport" ? "Airport transfer cancellation policy applies" : "Standard cancellation policy applies"}
                </span>
              </p>
            </div>

            {/* Traveller Details */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
              <form id="bookingForm" onSubmit={handleSubmit}>
                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 mb-6">
                  Traveller Details
                </h3>

                {/* Pickup & Drop */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pickup Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative flex items-center bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                      <FaMapMarkerAlt className="text-green-600 mr-3 text-sm flex-shrink-0" />
                      {isLoaded ? (
                        <Autocomplete
                          onLoad={(autocomplete) => (pickupAutocompleteRef.current = autocomplete)}
                          onPlaceChanged={onPlaceChangedPickup}
                          className="w-full"
                        >
                          <input
                            type="text"
                            value={pickupAddress}
                            onChange={(e) => setPickupAddress(e.target.value)}
                            placeholder={`Enter Pickup Location${fromCity ? ` in ${fromCity}` : ""}`}
                            disabled={serviceType === "airport" && airportDirection === "from"}
                            className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          value={pickupAddress}
                          onChange={(e) => setPickupAddress(e.target.value)}
                          placeholder={`Enter Pickup Location${fromCity ? ` in ${fromCity}` : ""}`}
                          disabled={serviceType === "airport" && airportDirection === "from"}
                          className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                        />
                      )}
                    </div>
                    {errors.pickupAddress && <p className="text-red-500 text-xs mt-1.5 font-semibold pl-1">{errors.pickupAddress}</p>}
                  </div>

                  {serviceType !== "rental" && !(serviceType === "airport" && airportDirection === "to") && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Drop Address
                      </label>
                      <div className="relative flex items-center bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                        <FaMapMarkerAlt className="text-red-500 mr-3 text-sm flex-shrink-0" />
                        {isLoaded ? (
                          <Autocomplete
                            onLoad={(autocomplete) => (dropAutocompleteRef.current = autocomplete)}
                            onPlaceChanged={onPlaceChangedDrop}
                            className="w-full"
                          >
                            <input
                              type="text"
                              value={dropAddress}
                              onChange={(e) => setDropAddress(e.target.value)}
                              placeholder={`Enter Drop Location${toCity ? ` in ${toCity}` : ""}`}
                              disabled={serviceType === "airport" && airportDirection === "to"}
                              className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                            />
                          </Autocomplete>
                        ) : (
                          <input
                            type="text"
                            value={dropAddress}
                            onChange={(e) => setDropAddress(e.target.value)}
                            placeholder={`Enter Drop Location${toCity ? ` in ${toCity}` : ""}`}
                            disabled={serviceType === "airport" && airportDirection === "to"}
                            className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Traveller Contact Header */}
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    Traveller Contact Details
                  </p>
                </div>

                {/* Name & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <div className="relative flex items-center bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                      <FaUser className="text-gray-400 mr-3 text-sm flex-shrink-0" />
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Full Name"
                        className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                      />
                    </div>
                    {errors.fullName && <p className="text-red-500 text-xs mt-1.5 font-semibold pl-1">{errors.fullName}</p>}
                  </div>
                  
                  <div>
                    <div className="relative flex items-center bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                      <FaPhone className="text-gray-400 mr-2 text-sm flex-shrink-0" />
                      <span className="text-gray-600 font-bold mr-2 text-[15px] border-r pr-2 border-gray-300">+91</span>
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                      />
                    </div>
                    {errors.mobile && <p className="text-red-500 text-xs mt-1.5 font-semibold pl-1">{errors.mobile}</p>}
                  </div>
                </div>

                {/* Email (Optional) */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-600">Email</span>
                    <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full">Optional</span>
                  </div>
                  <div className="relative flex items-center bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                    <FaEnvelope className="text-gray-400 mr-3 text-sm flex-shrink-0" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter Email id (for booking confirmation)"
                      className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400 font-medium text-[15px]"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1.5 font-semibold pl-1">{errors.email}</p>}
                </div>

                {/* Gender */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    Gender <span className="text-red-500">*</span>
                  </p>
                  <div className="flex items-center gap-3">
                    {["male", "female", "other"].map((g) => {
                      const isActive = gender === g;
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGender(g)}
                          className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-full border text-sm font-semibold transition-all active:scale-95 ${
                            isActive
                              ? "bg-blue-50 border-blue-600 text-blue-600 shadow-sm"
                              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {isActive && (
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          <span className="capitalize">{g}</span>
                        </button>
                      );
                    })}
                  </div>
                  {errors.gender && <p className="text-red-500 text-sm mt-1.5 font-medium">{errors.gender}</p>}
                </div>



                {/* Checkbox */}
                <div className="flex items-center gap-2 mb-4">
                  <input type="checkbox" defaultChecked id="billingCheckbox" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer" />
                  <label htmlFor="billingCheckbox" className="text-sm text-gray-700 font-medium cursor-pointer">
                    Use pickup location as billing address
                  </label>
                </div>


              </form>
            </div>
          </div>


          {/* RIGHT SIDE */}
          <div className="space-y-6">
            {/* Booking Info Banner Carousel */}
            <div className="w-full bg-[#FFF7ED] border border-[#FDBA74] rounded-xl px-4 py-2.5 shadow-xs">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <FaClock className="text-orange-600 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Carousel Messages - fully visible with natural height */}
                  <div className="relative min-h-[36px] flex items-center">
                    {[
                      "Cab operator will be assigned on booking completion.",
                      `Cab & driver details will be shared ${driverDetailsShareTimeStr} before ride.`,
                      `Free cancellation till ${freeCancellationTimeStr} of departure.`
                    ].map((msg, idx) => (
                      <p
                        key={idx}
                        className={`text-[13px] font-semibold text-gray-800 leading-snug transition-all duration-500 ${
                          idx === carouselIndex
                            ? 'opacity-100 relative'
                            : 'opacity-0 absolute inset-0 pointer-events-none'
                        }`}
                      >
                        {msg}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Modern inline dots indicator */}
                <div className="flex items-center gap-1 flex-shrink-0 pl-1">
                  {[0, 1, 2].map((idx) => (
                    <span
                      key={idx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        idx === carouselIndex ? 'w-3.5 bg-orange-500' : 'w-1.5 bg-orange-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Add on Service(Optional) */}
            {addOnServices && addOnServices.length > 0 && (
              <div className="w-full bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-600 tracking-wide">
                    ADD ON SERVICE(OPTIONAL)
                  </h3>
                </div>

                {/* Add-on Services List */}
                <div className="divide-y divide-gray-200">
                  {addOnServices.map((service, index) => (
                    <label key={index} className="flex items-start gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedAddOns.some(s => s.name === service.name)}
                        onChange={() => toggleAddOn(service)}
                        className="w-5 h-5 mt-0.5 accent-blue-600"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {service.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {service.description}
                        </p>
                      </div>
                      <div className="font-semibold text-gray-900 whitespace-nowrap ml-4">
                        ₹{service.price}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Coupon */}
            <div className="w-full bg-white rounded-[10.6px] shadow-md border border-gray-200 p-5">
              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Coupon & Offers
              </h3>

              {/* Applied Coupon Display */}
              {appliedCoupon && (
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="radio"
                    name="coupon"
                    checked
                    className="mt-1 accent-[#2FA19A]"
                    readOnly
                  />
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-2 border border-dashed border-[#2FA19A] text-[#2FA19A] px-3 py-1 rounded-md font-semibold text-sm">
                      <span>🎟</span>
                      {appliedCoupon.code}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      ₹{appliedCoupon.discount} off applied
                    </p>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-red-500 text-sm mt-1 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}

              {/* Available Coupons from API */}
              {!appliedCoupon && (
                <>
                  {coupons.filter(c => basePrice >= (c.minimumBookingAmount || 0)).length > 0 ? (
                    <>
                      {coupons
                        .filter(c => basePrice >= (c.minimumBookingAmount || 0))
                        .map((coupon, index, filteredArray) => (
                          <div key={coupon._id || index}>
                            <div
                              className="flex items-start gap-3 mb-4 cursor-pointer"
                              onClick={() => setAppliedCoupon({
                                code: coupon.code,
                                discount: coupon.discountType === 'percentage'
                                  ? Math.round(basePrice * (coupon.discountValue / 100))
                                  : coupon.discountValue
                              })}
                            >
                              <input
                                type="radio"
                                name="coupon"
                                checked={appliedCoupon?.code === coupon.code}
                                onChange={() => {
                                  const couponData = {
                                    code: coupon.code,
                                    discount: coupon.discountType === 'percentage'
                                      ? Math.round(basePrice * (coupon.discountValue / 100))
                                      : coupon.discountValue
                                  };
                                  setAppliedCoupon(couponData);
                                  setCouponCode("");
                                  // Save to sessionStorage
                                  sessionStorage.setItem("appliedCoupon", JSON.stringify(couponData));
                                }}
                                className="mt-1 accent-[#2FA19A]"
                              />
                              <div>
                                <div className="inline-flex items-center gap-2 border border-dashed border-[#2FA19A] text-[#2FA19A] px-3 py-1 rounded-md font-semibold text-sm">
                                  <span>🎟</span>
                                  {coupon.code}
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                  {coupon.description || `${coupon.discountType === 'percentage' ? coupon.discountValue + '%' : '₹' + coupon.discountValue} off`}
                                </p>
                              </div>
                            </div>
                            {index < filteredArray.length - 1 && <hr className="my-4 border-gray-200" />}
                          </div>
                        ))}
                      <hr className="my-4 border-gray-200" />
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">No coupons available</p>
                  )}
                </>
              )}

              {/* Enter Coupon */}
              {!appliedCoupon && (
                <div className="bg-[#FEE3D0] rounded-lg px-4 py-3 flex items-center justify-between">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="ENTER A COUPON"
                    className="bg-transparent outline-none text-sm font-medium text-gray-700 placeholder-gray-600 flex-1"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    className="text-gray-700 font-semibold text-sm ml-2"
                  >
                    APPLY
                  </button>
                </div>
              )}
            </div>

            {/* Unified Payment Option & Fare Breakup Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hidden md:block">
              <h3 className="text-[17px] font-bold text-gray-800 mb-5">Payment Option</h3>
              <div className="space-y-3">
                {!isCorporateBookingFlow ? (
                  <label className={`flex items-start justify-between cursor-pointer group p-3.5 rounded-xl border-2 transition-all duration-200 ${
                    paymentOption === "partial" 
                      ? "border-blue-600 bg-blue-50/40 shadow-sm" 
                      : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                  }`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="payment" checked={paymentOption === "partial"} onChange={() => setPaymentOption("partial")} className="w-4.5 h-4.5 mt-0.5 accent-blue-600" />
                      <div>
                        <p className="font-bold text-gray-900 text-[15px] leading-none mb-1">Part Pay</p>
                        <p className="text-[13px] text-gray-500 font-medium">Pay rest to the driver</p>
                      </div>
                    </div>
                    <span className="font-extrabold text-gray-900 text-[15px]">₹{partialAmount.toLocaleString("en-IN")}</span>
                  </label>
                ) : null}
 
                <label className={`flex items-start justify-between group p-3.5 rounded-xl border-2 transition-all duration-200 ${
                  isCorporateBookingFlow
                    ? "border-blue-600 bg-blue-50/40 shadow-sm cursor-default"
                    : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 cursor-pointer"
                }`}>
                  <div className="flex items-start gap-3">
                    {!isCorporateBookingFlow && (
                      <input type="radio" name="payment" checked={paymentOption === "full"} onChange={() => setPaymentOption("full")} className="w-4.5 h-4.5 mt-0.5 accent-blue-600" />
                    )}
                    <div>
                      <p className="font-bold text-gray-900 text-[15px] leading-none mb-1">Full Pay</p>
                      <p className="text-[13px] text-gray-500 font-medium">Full amount</p>
                    </div>
                  </div>
                  <span className="font-extrabold text-gray-900 text-[15px]">₹{finalTotal.toLocaleString("en-IN")}</span>
                </label>
              </div>
 
              <button
                type="button"
                onClick={handleSubmit}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white mt-5 py-4 rounded-xl font-black text-sm hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 active:scale-[0.98] transition-all duration-150 tracking-wider flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>PAY NOW</span>
                <span className="text-xs opacity-75 font-normal">•</span>
                <span className="font-extrabold text-[15px]">₹{amountToPay.toLocaleString("en-IN")}</span>
              </button>

              <hr className="my-5 border-gray-200" />

              <div
                className="flex justify-between items-center cursor-pointer mb-3"
                onClick={() => setIsFareBreakupOpen(!isFareBreakupOpen)}
              >
                <span className="text-[#2D68C4] font-bold text-[14px]">View Fare Break up</span>
                <svg className={`w-5 h-5 text-[#2D68C4] transform transition-transform ${isFareBreakupOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {isFareBreakupOpen && (
                <div className="bg-[#EEF2F9] rounded-xl p-4 text-[13px]">
                  <div className="space-y-2.5">
                    <div className="flex justify-between text-gray-800">
                      <span>Base Fare</span>
                      <span className="font-bold text-[14px]">₹{basePrice.toLocaleString("en-IN")}</span>
                    </div>

                    {couponDiscount > 0 && (
                      <div className="flex justify-between text-gray-800">
                        <span>Discount ({appliedCoupon?.code})</span>
                        <span className="font-bold text-red-600 text-[14px]">-₹{couponDiscount.toLocaleString("en-IN")}</span>
                      </div>
                    )}

                    {selectedAddOns && selectedAddOns.length > 0 ? (
                      selectedAddOns.map((addOn, idx) => (
                        <div key={idx} className="flex justify-between text-gray-800">
                          <span>{addOn.name}</span>
                          <span className="font-bold text-[14px]">₹{(addOn.price || 0).toLocaleString("en-IN")}</span>
                        </div>
                      ))
                    ) : addOnsCost > 0 ? (
                      <div className="flex justify-between text-gray-800">
                        <span>Driver Charges</span>
                        <span className="font-bold text-[14px]">₹{addOnsCost.toLocaleString("en-IN")}</span>
                      </div>
                    ) : null}

                    <div className="flex justify-between text-gray-800">
                      <span>Taxes & Fees</span>
                      <span className="font-bold text-[14px]">₹{gst.toLocaleString("en-IN")}</span>
                    </div>

                    <hr className="border-gray-200 my-2" />

                    <div className="flex justify-between items-center text-gray-900 mt-2">
                      <span className="font-medium text-[14px]">Total</span>
                      <span className="font-bold text-[16px]">₹{finalTotal.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Agreement Text */}
        <div className="text-[12px] text-gray-900 font-medium mt-10 mb-6 pl-1">
          By proceeding to book, I agree to MakeMyTrip's{" "}
          <Link to="/privacy" className="text-[#008cff] hover:underline">Privacy Policy</Link>,{" "}
          <Link to="/terms" className="text-[#008cff] hover:underline">User Agreement</Link>,{" "}
          <Link to="/terms" className="text-[#008cff] hover:underline">Terms of Service</Link>{" "}
          &{" "}
          <Link to="/cancellation-policy" className="text-[#008cff] hover:underline">Cancellation Rules</Link>
        </div>

        {/* Secured Payment Footer */}
        <div className="flex flex-col lg:flex-row items-center justify-between py-6 border-t border-gray-300 gap-6">
          <div className="flex items-center gap-3 w-max flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#008c85] flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="white" />
                <path d="M10 15l-3-3 1.4-1.4 1.6 1.6 4.6-4.6L16 9l-6 6z" fill="#008c85" />
              </svg>
            </div>
            <div>
              <p className="text-[#008c85] font-semibold text-[15px] leading-tight">CabZigo IS SECURED</p>
              <p className="text-[#008c85] font-semibold text-[15px] leading-tight">100% RBI Compliant</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 lg:mx-auto">
            {/* PCI DSS */}
            <div className="flex items-center">
              <div className="bg-[#145a55] text-white font-bold text-[12px] px-1 py-0.5 transform -skew-x-12">PCI</div>
              <div className="text-[#145a55] font-bold text-[12px] ml-1">
                <div className="leading-none">DSS</div>
                <div className="text-[7px] tracking-widest leading-none mt-[1px]">COMPLIANT</div>
              </div>
            </div>

            {/* Verisign */}
            <div className="flex items-center gap-1 border border-[#a6192e] rounded-full pl-0.5 pr-2 py-0.5">
              <span className="text-[#a6192e] w-4 h-4 flex items-center justify-center text-[14px] font-bold">✓</span>
              <div className="text-[#a6192e] text-[8px] leading-tight flex flex-col items-start border-l border-[#a6192e] pl-1">
                <span>VeriSign</span>
                <span>Secured</span>
              </div>
            </div>

            {/* Verified by VISA */}
            <div className="flex flex-col items-center italic text-[#1a3b82] leading-none">
              <span className="text-[9px] font-medium text-[#1a3b82]">Verified by</span>
              <span className="text-xl font-bold tracking-tighter">VISA</span>
            </div>

            {/* MasterCard SecureCode */}
            <div className="flex flex-col items-center leading-none">
              <span className="text-[#cc0000] font-bold italic text-[12px]">MasterCard.</span>
              <span className="text-[#ff9900] font-bold italic text-[10px]">SecureCode.</span>
            </div>

            {/* Amex SafeKey */}
            <div className="flex flex-col items-center leading-none -mt-1 lg:mr-4">
              <span className="text-[#1a3b82] font-bold text-[8px] tracking-wide uppercase">American Express</span>
              <span className="text-gray-500 font-bold text-[15px]">SafeKey<sup className="text-[8px]">®</sup></span>
            </div>
          </div>

          <div className="hidden lg:block h-12 w-[1px] bg-gray-400 mx-2"></div>

          <div className="max-w-[280px] text-[12px] text-gray-800 leading-[1.4] pr-1">
            By continuing to pay, I understand and agree with <br />
            <Link to="/terms" className="text-[#008cff] hover:underline">Terms of Service</Link>,{" "}
            <Link to="/privacy" className="text-[#008cff] hover:underline">Privacy Policy</Link> and{" "}
            <Link to="/terms" className="text-[#008cff] hover:underline">User Agreement</Link> <br />
            of <span className="text-[#008cff] font-medium">Cab</span><span className="text-orange-500 font-medium">Zigo</span>.
          </div>
        </div>
      </div>

      {/* MOBILE STICKY BOTTOM CHECKOUT BAR */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_10px_rgba(0,0,0,0.08)]">
        {/* Payment Option Toggle Row */}
        {!isCorporateBookingFlow && (
          <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-gray-100">
            <span className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mr-1">Pay:</span>
            <button
              type="button"
              onClick={() => setPaymentOption("partial")}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
                paymentOption === "partial"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Part Pay
              <span className={`text-[11px] font-extrabold ${paymentOption === "partial" ? "text-white/90" : "text-gray-500"}`}>
                ₹{partialAmount.toLocaleString("en-IN")}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentOption("full")}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
                paymentOption === "full"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Full Pay
              <span className={`text-[11px] font-extrabold ${paymentOption === "full" ? "text-white/90" : "text-gray-500"}`}>
                ₹{finalTotal.toLocaleString("en-IN")}
              </span>
            </button>
          </div>
        )}

        {/* Amount & Pay Button Row */}
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-xs text-gray-500 font-semibold">Total:</span>
              <span className="text-xl font-extrabold text-gray-900">
                ₹{amountToPay.toLocaleString("en-IN")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileBreakupOpen(true)}
              className="text-xs text-blue-600 font-bold hover:underline active:text-blue-700 block text-left"
            >
              View Breakup
            </button>
          </div>
          <button
            type="submit"
            form="bookingForm"
            className="bg-gradient-to-r from-orange-500 to-orange-600 active:from-orange-600 active:to-orange-700 text-white font-bold px-8 py-3 rounded-xl text-[15px] shadow-md transition-all active:scale-95 flex-shrink-0"
          >
            {paymentOption === "partial" ? "Pay Part" : "Proceed to Pay"}
          </button>
        </div>
      </div>

      {/* MOBILE FARE BREAKUP BOTTOM DRAWER */}
      {isMobileBreakupOpen && (
        <div className="md:hidden fixed inset-0 z-[100] overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 transition-opacity" 
            onClick={() => setIsMobileBreakupOpen(false)}
          ></div>
          
          {/* Bottom Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[24px] max-h-[85vh] overflow-y-auto shadow-[0_-8px_30px_rgb(0,0,0,0.15)] p-6 transition-transform duration-300 transform translate-y-0 z-10">
            {/* Handle Bar */}
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-5 cursor-pointer" onClick={() => setIsMobileBreakupOpen(false)}></div>
            
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-extrabold text-gray-900">Fare Breakup</h3>
              <button 
                type="button" 
                onClick={() => setIsMobileBreakupOpen(false)}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg p-1"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3.5 text-[13.5px] text-gray-800">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Base Fare</span>
                <span className="font-bold text-gray-900">₹{basePrice.toLocaleString("en-IN")}</span>
              </div>

              {couponDiscount > 0 && (
                <div className="flex justify-between items-center text-green-600 font-semibold">
                  <span>Coupon Discount ({appliedCoupon?.code})</span>
                  <span>-₹{couponDiscount.toLocaleString("en-IN")}</span>
                </div>
              )}

              {selectedAddOns && selectedAddOns.length > 0 ? (
                selectedAddOns.map((addOn, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-gray-600">{addOn.name}</span>
                    <span className="font-bold text-gray-900">₹{(addOn.price || 0).toLocaleString("en-IN")}</span>
                  </div>
                ))
              ) : addOnsCost > 0 ? (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Driver Charges</span>
                  <span className="font-bold text-gray-900">₹{addOnsCost.toLocaleString("en-IN")}</span>
                </div>
              ) : null}

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Taxes & Fees (18% GST)</span>
                <span className="font-bold text-gray-900">₹{gst.toLocaleString("en-IN")}</span>
              </div>

              <hr className="border-gray-100 my-3" />

              <div className="flex justify-between items-center text-gray-900">
                <span className="font-extrabold text-[15px]">Total Fare</span>
                <span className="font-extrabold text-lg text-gray-900">₹{finalTotal.toLocaleString("en-IN")}</span>
              </div>
              
              <hr className="border-gray-100 my-3" />
              
              <div className="bg-blue-50/70 rounded-xl p-4 space-y-2.5">
                <p className="font-bold text-blue-900 text-sm">Payment Details</p>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-blue-800 font-medium">Payment Option</span>
                  <span className="font-bold text-blue-900 capitalize">{paymentOption} Pay</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-blue-800 font-bold">Amount to Pay</span>
                  <span className="font-extrabold text-[16px] text-blue-900">₹{amountToPay.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
            
            <button
              type="button"
              onClick={() => setIsMobileBreakupOpen(false)}
              className="w-full mt-6 bg-gradient-to-r from-blue-900 to-indigo-900 hover:from-blue-800 hover:to-indigo-800 text-white font-bold py-3 rounded-xl transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CustomerDetails;
